import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Upload, ExternalLink, Trash2, Search, FileText, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const blank = { invoice_number: "", customer_user_id: "", amount: "", notes: "" };

export default function AccountsExternalInvoices() {
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blank);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/external-invoices", { params: { q: q || undefined } });
            setItems(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    // Load distributors + customers (so accounts staff can pick who the invoice belongs to)
    useEffect(() => {
        api.get("/admin/users").then((r) => {
            const list = r.data.filter((u) => ["customer", "distributor", "doctor"].includes(u.role));
            setUsers(list);
        }).catch(() => {});
    }, []);

    const submit = async () => {
        if (!file) return toast.error("Pick a PDF or image to upload");
        if (!form.invoice_number?.trim()) return toast.error("Invoice number is required");
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("invoice_number", form.invoice_number);
        if (form.customer_user_id) fd.append("customer_user_id", form.customer_user_id);
        if (form.amount) fd.append("amount", form.amount);
        if (form.notes) fd.append("notes", form.notes);
        try {
            await api.post("/external-invoices", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });
            toast.success("Invoice uploaded");
            setOpen(false); setForm(blank); setFile(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
        finally { setBusy(false); }
    };

    const del = async (inv) => {
        if (!window.confirm(`Delete ${inv.invoice_number}?`)) return;
        try { await api.delete(`/external-invoices/${inv.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const apiBase = (api.defaults.baseURL || "").replace(/\/api$/, "");

    return (
        <div data-testid="external-invoices" className="space-y-4">
            <Toaster position="top-center" />
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700">Billing</div>
                    <h1 className="font-serif text-4xl text-slate-900">External invoices</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-2xl">Upload bills/invoices generated outside this system (Tally, Excel, etc.). Linking to a customer makes it visible in their portal.</p>
                </div>
                <Button onClick={() => { setForm(blank); setFile(null); setOpen(true); }} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="upload-external">
                    <Upload className="w-4 h-4 mr-2" />Upload invoice
                </Button>
            </div>

            <div className="bg-white border border-sky-100 p-3 flex gap-2">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice #, customer name or email…" className="rounded-none border-stone-300 pl-9" data-testid="ext-search" />
                </div>
                <Button onClick={load} variant="outline" className="rounded-none">Search</Button>
            </div>

            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Invoice #</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Customer</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">File</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Amount</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Uploaded by</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">When</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={7} className="p-10 text-center text-stone-500">No external invoices yet.</td></tr>
                        ) : items.map((inv) => (
                            <tr key={inv.id} className="border-b border-stone-100" data-testid={`ext-row-${inv.id}`}>
                                <td className="p-3 font-mono">{inv.invoice_number}</td>
                                <td className="p-3">
                                    {inv.customer_name ? (
                                        <span>{inv.customer_name}<span className="block text-[11px] text-stone-500">{inv.customer_email}</span></span>
                                    ) : <span className="text-stone-400">unlinked</span>}
                                </td>
                                <td className="p-3">
                                    <a href={`${apiBase}${inv.file_url}`} target="_blank" rel="noreferrer" className="text-sky-700 hover:text-sky-800 inline-flex items-center text-xs underline" data-testid={`ext-view-${inv.id}`}>
                                        <FileText className="w-3.5 h-3.5 mr-1" />{inv.file_name}<ExternalLink className="w-3 h-3 ml-1" />
                                    </a>
                                </td>
                                <td className="p-3 text-right">{inv.amount != null ? formatINR(inv.amount) : "—"}</td>
                                <td className="p-3 text-stone-600 text-xs">{inv.uploaded_by_email}</td>
                                <td className="p-3 text-stone-500 text-xs">{new Date(inv.created_at).toLocaleString()}</td>
                                <td className="p-3 text-right">
                                    <button onClick={() => del(inv)} className="text-red-700 hover:text-red-800" data-testid={`ext-del-${inv.id}`}><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-none max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Upload external invoice</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">File (PDF / JPG / PNG / WEBP, max 10 MB) *</Label>
                            <Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-none border-stone-300 mt-1" data-testid="ext-file" />
                            {file && <div className="text-[11px] text-stone-500 mt-1">{file.name} · {Math.round(file.size / 1024)} KB</div>}
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Invoice number *</Label>
                            <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="rounded-none border-stone-300 mt-1" data-testid="ext-number" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Link to customer / distributor (optional)</Label>
                            <Select value={form.customer_user_id} onValueChange={(v) => setForm({ ...form, customer_user_id: v })}>
                                <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="ext-user"><SelectValue placeholder="(no linked user)" /></SelectTrigger>
                                <SelectContent>
                                    {users.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.name} <span className="text-stone-500 text-xs">· {u.role} · {u.email}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[10px] text-stone-500 mt-1">Linked users see this invoice in their portal &amp; can download it.</p>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Amount (₹, optional)</Label>
                            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-none border-stone-300 mt-1" data-testid="ext-amount" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Internal notes (optional)</Label>
                            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-none border-stone-300 mt-1" data-testid="ext-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                        <Button onClick={submit} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="ext-submit">
                            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : <><Upload className="w-4 h-4 mr-2" />Upload</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
